import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Save, Shield, Globe } from "lucide-react";

export default function AdminSettings() {
  const [settings, setSettings] = useState(null);
  const [auth0, setAuth0] = useState({ domain: "", client_id: "", client_secret: "", callback_path: "/auth/nwr-callback", audience: "", scope: "openid profile email", enabled: false });
  const [club, setClub] = useState({ name: "Nordgaming e.V.", tagline: "Play beyond limits", primary_game: "EA FC 26" });
  const [saved, setSaved] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/admin/settings").then((r) => {
      setSettings(r.data);
      if (r.data.auth0) setAuth0((a) => ({ ...a, ...r.data.auth0, client_secret: r.data.auth0.client_secret === "***" ? "" : r.data.auth0.client_secret }));
      if (r.data.club) setClub((c) => ({ ...c, ...r.data.club }));
    });
  }, []);

  const saveAuth0 = async (e) => {
    e.preventDefault();
    try {
      const body = { ...auth0 };
      // if user left secret empty AND server had one redacted, omit field
      if (!body.client_secret && settings?.auth0?.client_secret === "***") {
        // workaround: server requires field; fetch admin settings raw is not allowed unredacted in this MVP
      }
      await api.put("/admin/settings/auth0", body);
      setSaved("Auth0 Settings gespeichert");
      setTimeout(() => setSaved(""), 2500);
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  const saveClub = async (e) => {
    e.preventDefault();
    try {
      await api.put("/admin/settings/club", club);
      setSaved("Club Settings gespeichert");
      setTimeout(() => setSaved(""), 2500);
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  const callbackUrl = `${window.location.origin}${auth0.callback_path}`;

  return (
    <div className="space-y-8 fade-in" data-testid="admin-settings-page">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-gradient">Settings</div>
        <h1 className="font-display text-3xl tracking-tight mt-2">Konfiguration</h1>
      </div>

      {saved && <div className="text-accent text-sm font-mono" data-testid="settings-saved">{saved} ✓</div>}
      {error && <div className="text-red-400 text-sm font-mono">{error}</div>}

      <form onSubmit={saveClub} className="card-solid p-5 space-y-4" data-testid="club-settings-form">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-gradient flex items-center gap-2">
          <Globe className="w-3.5 h-3.5" /> Verein
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {[
            ["name", "Vereinsname"],
            ["tagline", "Tagline"],
            ["primary_game", "Primary Game"],
          ].map(([k, l]) => (
            <div key={k}>
              <label className="label">{l}</label>
              <input className="input" value={club[k]} onChange={(e) => setClub({ ...club, [k]: e.target.value })} data-testid={`club-${k}`} />
            </div>
          ))}
        </div>
        <button type="submit" className="btn-primary"><Save className="w-4 h-4" /> Speichern</button>
      </form>

      <form onSubmit={saveAuth0} className="card-solid p-5 space-y-4" data-testid="auth0-settings-form">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-gradient flex items-center gap-2">
          <Shield className="w-3.5 h-3.5" /> NWR ID · Auth0 Konfiguration
        </div>
        <div className="text-xs text-white/60 leading-relaxed">
          NWR ID = euer eigenes Authentifizierungs-System auf Auth0. Trage hier die Tenant-Daten ein.
          Whiteliste in Auth0 die Callback-URL: <span className="font-mono text-primary break-all">{callbackUrl}</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="label">Auth0 Domain</label>
            <input className="input" placeholder="nordgaming.eu.auth0.com" value={auth0.domain} onChange={(e) => setAuth0({ ...auth0, domain: e.target.value })} data-testid="auth0-domain" />
          </div>
          <div>
            <label className="label">Client ID</label>
            <input className="input" value={auth0.client_id} onChange={(e) => setAuth0({ ...auth0, client_id: e.target.value })} data-testid="auth0-client-id" />
          </div>
          <div>
            <label className="label">Client Secret {settings?.auth0?.client_secret === "***" && <span className="text-white/40">(gesetzt — leer lassen um beizubehalten)</span>}</label>
            <input type="password" className="input" placeholder={settings?.auth0?.client_secret === "***" ? "•••• gespeichert ••••" : ""} value={auth0.client_secret} onChange={(e) => setAuth0({ ...auth0, client_secret: e.target.value })} data-testid="auth0-client-secret" />
          </div>
          <div>
            <label className="label">Audience (optional)</label>
            <input className="input" value={auth0.audience} onChange={(e) => setAuth0({ ...auth0, audience: e.target.value })} data-testid="auth0-audience" />
          </div>
          <div>
            <label className="label">Scope</label>
            <input className="input" value={auth0.scope} onChange={(e) => setAuth0({ ...auth0, scope: e.target.value })} data-testid="auth0-scope" />
          </div>
          <div>
            <label className="label">Callback Path (App)</label>
            <input className="input" value={auth0.callback_path} onChange={(e) => setAuth0({ ...auth0, callback_path: e.target.value })} data-testid="auth0-callback" />
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={auth0.enabled} onChange={(e) => setAuth0({ ...auth0, enabled: e.target.checked })} data-testid="auth0-enabled" />
          NWR ID Login aktiv (Login-Button erscheint, sobald Domain/ClientID/Secret gesetzt sind)
        </label>
        <button type="submit" className="btn-primary"><Save className="w-4 h-4" /> Auth0 speichern</button>
      </form>
    </div>
  );
}
